import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from 'src/lib/utils';
import { useCancellationPlan } from 'src/context/billing';

import PlanUsageWarning from './PlanUsageWarning';
import ReviewAccountUsage from './ReviewAccountUsage';

const ActiveSolutionStepMap = {
  'show-plan-usage-warning': {
    title: 'Your account is still in active use',
    description:
      'Your Codekeeper account is currently protecting one or more software products. We can’t cancel your plan until you remove all active protections.',
    component: PlanUsageWarning,
    hasFooter: false,
  },
  'review-account-usage': {
    title: 'Review account use',
    description:
      'Your Codekeeper account is currently protecting one or more software products. We can’t cancel your plan until you terminate all active protections.',
    component: ReviewAccountUsage,
    hasFooter: true,
  },
};

const ActiveSolutionsDialog = () => {
  const {
    ui: { step, setStep, setInitialData },
  } = useCancellationPlan();

  const isOpen = Object.keys(ActiveSolutionStepMap).includes(step ?? '');

  const onOpenChange = (open: boolean) => {
    if (!open) setInitialData();
  };

  const onBackToPlanUsageWarning = () => setStep('show-plan-usage-warning');

  if (!step || !(step in ActiveSolutionStepMap)) return null;

  const {
    title,
    description,
    component: ActiveComponent,
    hasFooter,
  } = ActiveSolutionStepMap[step as keyof typeof ActiveSolutionStepMap];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'sm:max-w-130 gap-6 min-h-0 max-h-205',
          hasFooter ? 'grid-rows-[auto_1fr_auto]' : 'grid-rows-[auto_1fr]',
        )}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap">{description}</DialogDescription>
        </DialogHeader>
        <ActiveComponent />
        {hasFooter && (
          <DialogFooter className="flex-row! justify-between! -mx-6 -mb-6 px-6 py-4 border-t border-border">
            <Button variant="ghost" onClick={onBackToPlanUsageWarning}>
              <ArrowLeft /> Back
            </Button>
            <Button variant="outline" onClick={setInitialData}>
              Close
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActiveSolutionsDialog;
